<template>
    <v-container fluid class="pa-4">
        <div class="d-flex align-center mb-4">
            <v-btn icon variant="text" size="small" class="mr-2" @click="$router.push('/business-dashboard')">
                <v-icon>mdi-arrow-left</v-icon>
            </v-btn>
            <v-icon color="orange" size="28" class="mr-3">mdi-cog-outline</v-icon>
            <div>
                <h1 class="text-h5 font-weight-bold">Operations & Health</h1>
                <p class="text-caption text-medium-emphasis mb-0">Payment health, device fleet, subscriptions, and error tracking</p>
            </div>
            <v-spacer/>
            <v-select v-model="months" :items="periodOptions" item-title="label" item-value="value"
                      density="compact" variant="outlined" hide-details style="max-width: 160px" class="mr-2"/>
            <v-btn variant="text" :loading="loading" @click="loadData" prepend-icon="mdi-refresh">Refresh</v-btn>
        </div>

        <v-overlay :model-value="loading && !data" contained class="align-center justify-center">
            <v-progress-circular indeterminate size="48"/>
        </v-overlay>

        <v-alert v-if="error" type="error" variant="tonal" closable class="mb-4" @click:close="error = null">
            {{ error }}
            <template v-slot:append>
                <v-btn variant="text" size="small" @click="loadData">Retry</v-btn>
            </template>
        </v-alert>

        <template v-if="data">
            <!-- Alert Cards -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="3">
                    <v-card elevation="2" class="pa-4" :color="data.companies_no_payment > 0 ? '' : ''">
                        <div class="d-flex align-center mb-1">
                            <v-avatar color="red" size="36" variant="tonal">
                                <v-icon color="red" size="20">mdi-credit-card-off</v-icon>
                            </v-avatar>
                            <v-spacer/>
                        </div>
                        <div class="text-h5 font-weight-bold">{{ data.companies_no_payment }}</div>
                        <div class="text-caption text-medium-emphasis">Companies Without Payment Method</div>
                    </v-card>
                </v-col>
                <v-col cols="12" md="3">
                    <v-card elevation="2" class="pa-4">
                        <div class="d-flex align-center mb-1">
                            <v-avatar color="orange" size="36" variant="tonal">
                                <v-icon color="orange" size="20">mdi-shield-off</v-icon>
                            </v-avatar>
                        </div>
                        <div class="text-h5 font-weight-bold">{{ data.bypass_payment_devices }}</div>
                        <div class="text-caption text-medium-emphasis">Devices with Payment Bypass</div>
                    </v-card>
                </v-col>
                <v-col cols="12" md="3">
                    <v-card elevation="2" class="pa-4">
                        <div class="d-flex align-center mb-1">
                            <v-avatar color="blue" size="36" variant="tonal">
                                <v-icon color="blue" size="20">mdi-router-wireless</v-icon>
                            </v-avatar>
                        </div>
                        <div class="text-h5 font-weight-bold">
                            {{ (data.device_status?.active || 0) + (data.device_status?.disabled || 0) + (data.device_status?.faulty || 0) + (data.device_status?.lost || 0) }}
                        </div>
                        <div class="text-caption text-medium-emphasis">Total Activated Devices</div>
                    </v-card>
                </v-col>
                <v-col cols="12" md="3">
                    <v-card elevation="2" class="pa-4">
                        <div class="d-flex align-center mb-1">
                            <v-avatar color="teal" size="36" variant="tonal">
                                <v-icon color="teal" size="20">mdi-credit-card-check</v-icon>
                            </v-avatar>
                        </div>
                        <div class="text-h5 font-weight-bold">
                            {{ data.payment_method_brands.reduce((sum, b) => sum + b.count, 0) }}
                        </div>
                        <div class="text-caption text-medium-emphasis">Active Payment Methods</div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Payment Failures & Errors Trend -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="red" size="20" class="mr-2">mdi-alert-circle</v-icon>
                            Payment Failures (Monthly)
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="paymentFailureChartData" :options="redBarOptions" style="height: 260px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="orange" size="20" class="mr-2">mdi-bug</v-icon>
                            Registration & Activation Errors
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="errorChartData" :options="stackedBarOptions" style="height: 260px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Device Status & SKU Distribution -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="4">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="blue" size="20" class="mr-2">mdi-chart-donut</v-icon>
                            Device Status
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Doughnut :data="deviceStatusChartData" :options="doughnutOptions" style="height: 240px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="purple" size="20" class="mr-2">mdi-cpu-64-bit</v-icon>
                            Devices by Product
                        </v-card-title>
                        <v-divider/>
                        <v-card-text class="pa-0">
                            <v-table density="compact">
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>SKU</th>
                                        <th class="text-right">Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="sku in data.devices_by_sku" :key="sku.sku">
                                        <td class="text-body-2">{{ sku.product_name }}</td>
                                        <td class="text-caption text-medium-emphasis">{{ sku.sku }}</td>
                                        <td class="text-right font-weight-bold">{{ sku.count }}</td>
                                    </tr>
                                    <tr v-if="data.devices_by_sku.length === 0">
                                        <td colspan="3" class="text-center text-medium-emphasis">No data</td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="4">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="indigo" size="20" class="mr-2">mdi-credit-card</v-icon>
                            Payment Methods by Brand
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Doughnut v-if="data.payment_method_brands.length > 0"
                                      :data="paymentBrandChartData" :options="doughnutOptions" style="height: 240px"/>
                            <div v-else class="text-center text-medium-emphasis py-8">No payment methods</div>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Subscription Plans & Expiry Timeline -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="teal" size="20" class="mr-2">mdi-clipboard-list</v-icon>
                            Active Devices by Subscription Plan
                        </v-card-title>
                        <v-divider/>
                        <v-card-text class="pa-0">
                            <v-table density="compact">
                                <thead>
                                    <tr>
                                        <th>Plan</th>
                                        <th class="text-right">Price</th>
                                        <th class="text-right">Interval</th>
                                        <th class="text-right">Devices</th>
                                        <th class="text-right">Est. Monthly</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="plan in data.plan_distribution" :key="plan.plan_name">
                                        <td>{{ plan.plan_name || 'No Plan' }}</td>
                                        <td class="text-right">${{ Number(plan.price || 0).toFixed(2) }}</td>
                                        <td class="text-right">{{ intervalName(plan.interval) }}</td>
                                        <td class="text-right font-weight-bold">{{ plan.device_count }}</td>
                                        <td class="text-right font-weight-bold text-green">
                                            ${{ estimateMonthly(plan).toLocaleString('en-US', {minimumFractionDigits: 2}) }}
                                        </td>
                                    </tr>
                                    <tr v-if="data.plan_distribution.length > 0" class="bg-grey-lighten-4">
                                        <td colspan="3" class="font-weight-bold">Total</td>
                                        <td class="text-right font-weight-bold">
                                            {{ data.plan_distribution.reduce((s, p) => s + p.device_count, 0) }}
                                        </td>
                                        <td class="text-right font-weight-bold text-green">
                                            ${{ data.plan_distribution.reduce((s, p) => s + estimateMonthly(p), 0).toLocaleString('en-US', {minimumFractionDigits: 2}) }}
                                        </td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="orange" size="20" class="mr-2">mdi-calendar-clock</v-icon>
                            Subscription Expiry Timeline (Next 6 Months)
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="expiryChartData" :options="expiryBarOptions" style="height: 260px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Payment Sources -->
            <v-row dense v-if="data.payment_sources.length > 0">
                <v-col cols="12" md="6">
                    <v-card elevation="2">
                        <v-card-title class="py-3">
                            <v-icon color="green" size="20" class="mr-2">mdi-source-branch</v-icon>
                            Invoice Payments by Source
                        </v-card-title>
                        <v-divider/>
                        <v-card-text class="pa-0">
                            <v-table density="compact">
                                <thead>
                                    <tr>
                                        <th>Source</th>
                                        <th class="text-right">Payments</th>
                                        <th class="text-right">Amount</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="source in data.payment_sources" :key="source.client_source">
                                        <td>
                                            <v-chip size="small" :color="source.client_source === 'web' ? 'blue' : source.client_source === 'app' ? 'green' : 'grey'" variant="tonal">
                                                {{ source.client_source }}
                                            </v-chip>
                                        </td>
                                        <td class="text-right">{{ source.count }}</td>
                                        <td class="text-right font-weight-bold">${{ Number(source.total_amount).toLocaleString('en-US', {minimumFractionDigits: 2}) }}</td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- ── Message Gateway & System Performance ──────────────────── -->
            <v-row dense class="mt-4">
                <v-col cols="12">
                    <div class="d-flex align-center mb-2">
                        <v-icon color="deep-purple" size="22" class="mr-2">mdi-transit-connection-variant</v-icon>
                        <span class="text-subtitle-1 font-weight-bold">Message Gateway &amp; System Performance</span>
                        <v-spacer/>
                        <!-- period toggle: today / week / month -->
                        <v-btn-toggle v-model="gwPeriod" density="compact" variant="outlined" mandatory>
                            <v-btn value="today" size="small">Today</v-btn>
                            <v-btn value="week"  size="small">Week</v-btn>
                            <v-btn value="month" size="small">Month</v-btn>
                        </v-btn-toggle>
                    </div>
                </v-col>
            </v-row>

            <!-- Gateway KPI tiles -->
            <v-row dense class="mb-3">
                <v-col cols="6" sm="4" md="2" v-for="tile in gwKpiTiles" :key="tile.key">
                    <v-card elevation="2" class="pa-3 text-center">
                        <v-icon :color="tile.color" size="22" class="mb-1">{{ tile.icon }}</v-icon>
                        <div class="text-h6 font-weight-bold">{{ formatCount(tile.value) }}</div>
                        <div class="text-caption text-medium-emphasis">{{ tile.label }}</div>
                    </v-card>
                </v-col>

                <!-- TX ACK rate tile -->
                <v-col cols="6" sm="4" md="2" v-if="data.gateway_performance?.tx_ack">
                    <v-card elevation="2" class="pa-3 text-center">
                        <v-icon :color="ackRateColor" size="22" class="mb-1">mdi-check-network</v-icon>
                        <div class="text-h6 font-weight-bold" :class="`text-${ackRateColor}`">
                            {{ data.gateway_performance.tx_ack.rate_pct }}%
                        </div>
                        <div class="text-caption text-medium-emphasis">TX ACK Rate (month)</div>
                    </v-card>
                </v-col>

                <!-- Avg payload size tile -->
                <v-col cols="6" sm="4" md="2">
                    <v-card elevation="2" class="pa-3 text-center">
                        <v-icon color="cyan" size="22" class="mb-1">mdi-database-arrow-up</v-icon>
                        <div class="text-h6 font-weight-bold">{{ data.gateway_performance?.avg_payload_bytes || 0 }} B</div>
                        <div class="text-caption text-medium-emphasis">Avg Payload Size (month)</div>
                    </v-card>
                </v-col>

                <!-- FCM token tile -->
                <v-col cols="6" sm="4" md="2" v-if="totalPushTokens > 0">
                    <v-card elevation="2" class="pa-3 text-center">
                        <v-icon color="teal" size="22" class="mb-1">mdi-cellphone-wireless</v-icon>
                        <div class="text-h6 font-weight-bold">{{ formatCount(totalPushTokens) }}</div>
                        <div class="text-caption text-medium-emphasis">Active FCM Tokens</div>
                    </v-card>
                </v-col>
            </v-row>

            <!-- RX/TX daily trend + Alert channel trend -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="blue" size="20" class="mr-2">mdi-swap-vertical</v-icon>
                            Device RX / TX (last 30 days)
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="rxTxTrendChartData" :options="rxTxTrendOptions" style="height: 260px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
                <v-col cols="12" md="6">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="deep-purple" size="20" class="mr-2">mdi-bell-ring</v-icon>
                            Alert Notifications by Channel (last 30 days)
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="alertChannelTrendChartData" :options="alertChannelTrendOptions" style="height: 260px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>

            <!-- Alerts by type + Notification channel config -->
            <v-row dense class="mb-4">
                <v-col cols="12" md="4">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="deep-purple" size="20" class="mr-2">mdi-bell-alert</v-icon>
                            Alerts Fired by Type
                        </v-card-title>
                        <v-divider/>
                        <v-card-text class="pa-0">
                            <v-table density="compact">
                                <thead>
                                    <tr>
                                        <th>Type</th>
                                        <th class="text-right">Count</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="a in data.alerts_by_type" :key="a.alert_type">
                                        <td>
                                            <v-chip size="x-small" variant="tonal" class="mr-1">{{ a.alert_type }}</v-chip>
                                            {{ a.label || alertTypeLabel(a.alert_type) }}
                                        </td>
                                        <td class="text-right font-weight-bold">{{ a.count.toLocaleString() }}</td>
                                    </tr>
                                    <tr v-if="!data.alerts_by_type?.length">
                                        <td colspan="2" class="text-center text-medium-emphasis py-4">No alerts in period</td>
                                    </tr>
                                </tbody>
                            </v-table>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" md="4">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="teal" size="20" class="mr-2">mdi-account-group</v-icon>
                            Notification Recipients Configured
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <div class="d-flex flex-column" style="gap:12px">
                                <div v-for="ch in notifChannelRows" :key="ch.label" class="d-flex align-center justify-space-between">
                                    <div class="d-flex align-center" style="gap:8px">
                                        <v-icon :color="ch.color" size="20">{{ ch.icon }}</v-icon>
                                        <span class="text-body-2">{{ ch.label }}</span>
                                    </div>
                                    <span class="font-weight-bold text-body-1">{{ ch.count }}</span>
                                </div>
                            </div>
                        </v-card-text>
                    </v-card>
                </v-col>

                <v-col cols="12" md="4">
                    <v-card elevation="2" class="fill-height">
                        <v-card-title class="py-3">
                            <v-icon color="deep-purple" size="20" class="mr-2">mdi-chart-line</v-icon>
                            Alert Volume Trend (monthly)
                        </v-card-title>
                        <v-divider/>
                        <v-card-text>
                            <Bar :data="alertTrendChartData" :options="alertTrendOptions" style="height: 220px"/>
                        </v-card-text>
                    </v-card>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>

<script setup>
import {ref, computed, onMounted, watch} from 'vue';
import {send} from '@/composables/apiClient';
import {alertTypeLabel} from '@/composables/alertTypes';
import {Bar, Doughnut} from 'vue-chartjs';
import {Chart, registerables} from 'chart.js';

Chart.register(...registerables);

const loading = ref(false);
const data = ref(null);
const error = ref(null);
const months = ref(12);
const gwPeriod = ref('week');
const periodOptions = [
    {label: 'Last 6 months', value: 6},
    {label: 'Last 12 months', value: 12},
    {label: 'Last 24 months', value: 24},
    {label: 'Last 36 months', value: 36},
];

function monthLabel(year, month) {
    return new Date(year, month - 1).toLocaleDateString('en-US', {month: 'short', year: '2-digit'});
}

function intervalName(interval) {
    return {0: 'Monthly', 1: 'Quarterly', 2: 'Yearly'}[interval] || 'N/A';
}

function estimateMonthly(plan) {
    const price = Number(plan.price || 0);
    const divisor = {0: 1, 1: 3, 2: 12}[plan.interval] || 1;
    return (price / divisor) * plan.device_count;
}

const statusColors = {active: '#4CAF50', disabled: '#9E9E9E', faulty: '#FF9800', lost: '#F44336'};

const deviceStatusChartData = computed(() => {
    if (!data.value?.device_status) return {labels: [], datasets: [{data: []}]};
    const s = data.value.device_status;
    const entries = Object.entries(s).filter(([, v]) => v > 0);
    return {
        labels: entries.map(([k]) => k.charAt(0).toUpperCase() + k.slice(1)),
        datasets: [{
            data: entries.map(([, v]) => v),
            backgroundColor: entries.map(([k]) => statusColors[k] || '#607D8B'),
        }],
    };
});

const paymentFailureChartData = computed(() => {
    if (!data.value?.payment_failures) return {labels: [], datasets: [{data: []}]};
    const f = data.value.payment_failures;
    return {
        labels: f.map(r => monthLabel(r.year, r.month)),
        datasets: [{
            label: 'Payment Failures',
            data: f.map(r => r.count),
            backgroundColor: '#F44336',
        }],
    };
});

const errorChartData = computed(() => {
    if (!data.value) return {labels: [], datasets: [{data: []}]};
    const reg = data.value.registration_errors || [];
    const act = data.value.activation_errors || [];
    // Merge labels
    const allMonths = new Map();
    [...reg, ...act].forEach(r => allMonths.set(`${r.year}-${r.month}`, {year: r.year, month: r.month}));
    const sorted = [...allMonths.values()].sort((a, b) => a.year - b.year || a.month - b.month);
    const labels = sorted.map(r => monthLabel(r.year, r.month));

    const regMap = new Map(reg.map(r => [`${r.year}-${r.month}`, r.count]));
    const actMap = new Map(act.map(r => [`${r.year}-${r.month}`, r.count]));

    return {
        labels,
        datasets: [
            {
                label: 'Registration Errors',
                data: sorted.map(r => regMap.get(`${r.year}-${r.month}`) || 0),
                backgroundColor: '#FF9800',
                stack: 'errors',
            },
            {
                label: 'Activation Errors',
                data: sorted.map(r => actMap.get(`${r.year}-${r.month}`) || 0),
                backgroundColor: '#F44336',
                stack: 'errors',
            },
        ],
    };
});

const paymentBrandChartData = computed(() => {
    if (!data.value?.payment_method_brands) return {labels: [], datasets: [{data: []}]};
    const brands = data.value.payment_method_brands;
    const brandColors = {visa: '#1A1F71', mastercard: '#EB001B', amex: '#006FCF', discover: '#FF6000'};
    return {
        labels: brands.map(b => (b.brand || 'Unknown').charAt(0).toUpperCase() + (b.brand || 'unknown').slice(1)),
        datasets: [{
            data: brands.map(b => b.count),
            backgroundColor: brands.map(b => brandColors[b.brand?.toLowerCase()] || '#607D8B'),
        }],
    };
});

const expiryChartData = computed(() => {
    if (!data.value?.expiry_timeline) return {labels: [], datasets: [{data: []}]};
    const e = data.value.expiry_timeline;
    return {
        labels: e.map(r => monthLabel(r.year, r.month)),
        datasets: [{
            label: 'Expiring Devices',
            data: e.map(r => r.count),
            backgroundColor: '#FF9800',
        }],
    };
});

const doughnutOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {position: 'bottom'}},
};

const redBarOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {display: false}},
    scales: {y: {beginAtZero: true}},
};

const stackedBarOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {position: 'top'}},
    scales: {x: {stacked: true}, y: {stacked: true, beginAtZero: true}},
};

const expiryBarOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {display: false}},
    scales: {y: {beginAtZero: true}},
};

// ── Gateway KPI tiles ──────────────────────────────────────────────────
function formatCount(n) {
    if (!n) return '0';
    if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + 'M';
    if (n >= 1_000)     return (n / 1_000).toFixed(1) + 'K';
    return String(n);
}

const gwKpiTiles = computed(() => {
    const gw = data.value?.gateway_performance;
    const al = gw?.alerts;
    const p  = gwPeriod.value;
    if (!gw) return [];
    return [
        {key: 'rx',    icon: 'mdi-arrow-down-circle-outline', color: 'blue',         label: 'RX (device→server)', value: gw.rx?.[p]   ?? 0},
        {key: 'tx',    icon: 'mdi-arrow-up-circle-outline',   color: 'indigo',       label: 'TX (server→device)', value: gw.tx?.[p]   ?? 0},
        {key: 'push',  icon: 'mdi-bell-outline',              color: 'green',        label: 'Push Sent',          value: al?.[`${p}_push`]  ?? 0},
        {key: 'email', icon: 'mdi-email-outline',             color: 'blue-darken-2',label: 'Emails Sent',        value: al?.[`${p}_email`] ?? 0},
        {key: 'sms',   icon: 'mdi-message-text-outline',      color: 'orange',       label: 'SMS Sent',           value: al?.[`${p}_sms`]   ?? 0},
        {key: 'total', icon: 'mdi-alert-outline',             color: 'red',          label: 'Total Alerts',       value: al?.[`${p}_total`] ?? 0},
    ];
});

const ackRateColor = computed(() => {
    const rate = data.value?.gateway_performance?.tx_ack?.rate_pct;
    if (rate == null) return 'grey';
    if (rate >= 95)   return 'green';
    if (rate >= 85)   return 'orange';
    return 'red';
});

const totalPushTokens = computed(() =>
    (data.value?.push_tokens ?? []).reduce((s, t) => s + Number(t.count), 0)
);

const notifChannelRows = computed(() => {
    const ch = data.value?.notification_channels ?? {};
    const pt = data.value?.push_tokens ?? [];
    const rows = [
        {label: 'App Push (in-app)',     icon: 'mdi-bell-outline',         color: 'green',  count: ch.push_count  || 0},
        {label: 'Email',                 icon: 'mdi-email-outline',        color: 'blue',   count: ch.email_count || 0},
        {label: 'SMS',                   icon: 'mdi-message-text-outline', color: 'orange', count: ch.sms_count   || 0},
    ];
    pt.forEach(t => rows.push({
        label: `FCM tokens (${t.platform})`,
        icon:  t.platform === 'ios' ? 'mdi-apple' : 'mdi-android',
        color: t.platform === 'ios' ? 'grey' : 'green',
        count: t.count,
    }));
    return rows;
});

// ── RX / TX daily trend chart ──────────────────────────────────────────
const rxTxTrendChartData = computed(() => {
    const rows = data.value?.rx_tx_trend ?? [];
    // Parse YYYY-MM-DD as local date (not UTC) to avoid day-shift for US timezones
    const labels = rows.map(r => { const [y,m,d] = r.date.split('-'); return new Date(y, m-1, d).toLocaleDateString('en-US', {month: 'short', day: 'numeric'}); });
    return {
        labels,
        datasets: [
            {label: 'RX (from device)', data: rows.map(r => r.rx_count), backgroundColor: '#2196F3', stack: 'pkt'},
            {label: 'TX (to device)',   data: rows.map(r => r.tx_count), backgroundColor: '#3F51B5', stack: 'pkt'},
        ],
    };
});
const rxTxTrendOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {position: 'top'}},
    scales: {x: {stacked: true}, y: {stacked: true, beginAtZero: true}},
};

// ── Alert channel daily trend chart ───────────────────────────────────
const alertChannelTrendChartData = computed(() => {
    const rows = data.value?.alert_channel_trend ?? [];
    // Parse YYYY-MM-DD as local date (not UTC) to avoid day-shift for US timezones
    const labels = rows.map(r => { const [y,m,d] = r.date.split('-'); return new Date(y, m-1, d).toLocaleDateString('en-US', {month: 'short', day: 'numeric'}); });
    return {
        labels,
        datasets: [
            {label: 'Push',  data: rows.map(r => r.push_count),  backgroundColor: '#4CAF50', stack: 'ch'},
            {label: 'Email', data: rows.map(r => r.email_count), backgroundColor: '#2196F3', stack: 'ch'},
            {label: 'SMS',   data: rows.map(r => r.sms_count),   backgroundColor: '#FF9800', stack: 'ch'},
        ],
    };
});
const alertChannelTrendOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {position: 'top'}},
    scales: {x: {stacked: true}, y: {stacked: true, beginAtZero: true}},
};

// ── Monthly alert volume trend ─────────────────────────────────────────
const alertTrendChartData = computed(() => {
    if (!data.value?.alert_trend) return {labels: [], datasets: [{data: []}]};
    const t = data.value.alert_trend;
    return {
        labels: t.map(r => monthLabel(r.year, r.month)),
        datasets: [{label: 'Alerts', data: t.map(r => r.count), backgroundColor: '#7C3AED'}],
    };
});
const alertTrendOptions = {
    responsive: true, maintainAspectRatio: false,
    plugins: {legend: {display: false}},
    scales: {y: {beginAtZero: true}},
};

async function loadData() {
    loading.value = true;
    error.value = null;
    try {
        const response = await send('get', 'admin/reports/operations', {months: months.value});
        if (response?.data?.data) {
            data.value = response.data.data;
        } else {
            error.value = response?.data?.message || 'No data returned';
        }
    } catch (e) {
        console.error('Failed to load operations data', e);
        error.value = e?.response?.data?.message || e?.message || 'Failed to load operations data';
    } finally {
        loading.value = false;
    }
}

watch(months, () => loadData());
onMounted(loadData);
</script>
